import type { UserActivitiesResponse } from "@/types/user-activities";
import { authorizedFetcherGet } from "@/utils/request";
import { useMemo, useState } from "react";
import useSWRImmutable from "swr/immutable";
import { useAccessToken } from "./useAccessToken";

export const useUserActivities = (walletAddress: string | null) => {
  const getAccessToken = useAccessToken();

  const { data, error, isLoading } = useSWRImmutable<UserActivitiesResponse>(
    walletAddress ? "/v2/rewards/activities" : null,
    (path: string) =>
      authorizedFetcherGet<UserActivitiesResponse>(path, getAccessToken),
  );

  const activities = useMemo(() => {
    if (!data?.result?.length) return [];

    return data.result.filter(
      (activity) =>
        activity.reward.reward_type === "gem" &&
        activity.reward.amount !== 0 &&
        activity.reward.claimed_at !== null,
    );
  }, [data]);

  const storageKey = useMemo(
    () => (walletAddress ? `last-read-activity-${walletAddress}` : ""),
    [walletAddress],
  );

  const getSevenDaysAgo = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString();
  };

  const [lastReadTimestamp, setLastReadTimestamp] = useState<string>(
    localStorage.getItem(storageKey) || getSevenDaysAgo(),
  );

  const unreadCount = useMemo(() => {
    return activities?.filter(
      (activity) => activity.occurred_at > lastReadTimestamp,
    ).length;
  }, [activities, lastReadTimestamp]);

  const markTopActivityAsRead = (): void => {
    if (activities?.length) {
      const topActivity = activities[0];
      localStorage.setItem(storageKey, topActivity.occurred_at);
      setLastReadTimestamp(topActivity.occurred_at);
    }
  };

  return {
    activities,
    unreadCount,
    error,
    isLoading,
    markTopActivityAsRead,
  };
};
